import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "../../../components/feature/Navbar";
import Footer from "../../../components/feature/Footer";
import { fetchCategoryById, CategoryDB } from "../../../hooks/useCategories";
import { supabase } from "../../../lib/supabase";

type Tab = "providers" | "courses";

interface ProviderRow {
  id: string;
  name: string;
  service_name: string | null;
  bio: string | null;
  slogan: string | null;
  location: string | null;
  avatar_url: string | null;
  cover_image_url: string | null;
  skills: string[] | null;
  rating: number;
  review_count: number;
  follower_count: number;
  plan: string;
}

interface ProductRow {
  id: string;
  title: string;
  price: number;
  type: string;
  thumbnail_url: string | null;
  provider_id: string;
  provider?: { name: string; service_name: string | null };
}

export default function CategoryDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<Tab>("providers");
  const [sort, setSort] = useState("popular");
  const [category, setCategory] = useState<CategoryDB | null>(null);
  const [providers, setProviders] = useState<ProviderRow[]>([]);
  const [courses, setCourses] = useState<ProductRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      setLoading(true);
      const cat = await fetchCategoryById(id);
      setCategory(cat);
      if (cat) {
        const [{ data: provData }, { data: prodData }] = await Promise.all([
          supabase.from("profiles").select("id,name,service_name,bio,slogan,location,avatar_url,cover_image_url,skills,rating,review_count,follower_count,plan")
            .eq("role", "provider").eq("category", cat.name),
          supabase.from("products").select("id,title,price,type,thumbnail_url,provider_id,provider:provider_id(name,service_name)")
            .eq("category", cat.name).eq("visibility", "public").eq("status", "public"),
        ]);
        setProviders((provData || []) as ProviderRow[]);
        setCourses((prodData || []) as ProductRow[]);
      }
      setLoading(false);
    };
    load();
  }, [id]);

  // SEO: update meta tags when category loads
  useEffect(() => {
    if (!category) return;
    const title = `${category.name}の職人・専門家 | TSUGITE`;
    const description = `${category.name}のスペシャリストから学ぶ動画講座・教材キット。${category.description || ""}TSUGITEで職人・専門家に直接学べます。`;
    document.title = title;

    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta("description", description);
    setMeta("keywords", `${category.name}, 職人, 専門家, 動画講座, 教材, TSUGITE`);
    setMeta("og:title", title, true);
    setMeta("og:description", description, true);
    setMeta("og:type", "website", true);
    setMeta("og:url", `https://tsugite.jp/categories/${id}`, true);
    if (category.image) setMeta("og:image", category.image, true);
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);

    // Schema.org CollectionPage
    let schemaEl = document.getElementById("schema-category");
    if (!schemaEl) {
      schemaEl = document.createElement("script");
      schemaEl.setAttribute("type", "application/ld+json");
      schemaEl.id = "schema-category";
      document.head.appendChild(schemaEl);
    }
    schemaEl.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": `${category.name} | TSUGITE`,
      "description": description,
      "url": `https://tsugite.jp/categories/${id}`,
      "image": category.image,
    });

    return () => {
      document.title = "TSUGITE | 職人・専門家から学ぶプラットフォーム";
      if (schemaEl) schemaEl.remove();
    };
  }, [category, id]);

  const sortedProviders = [...providers].sort((a, b) =>
    sort === "popular" ? (b.follower_count ?? 0) - (a.follower_count ?? 0) : (b.rating ?? 0) - (a.rating ?? 0)
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-ivory">
        <Navbar />
        <div className="h-72 bg-stone-200 animate-pulse mt-16" />
        <div className="max-w-screen-xl mx-auto px-4 py-8 space-y-4">
          {[...Array(3)].map((_, i) => <div key={i} className="h-40 bg-stone-200 rounded-2xl animate-pulse" />)}
        </div>
        <Footer />
      </div>
    );
  }

  if (!category) {
    return (
      <div className="min-h-screen bg-ivory flex items-center justify-center">
        <div className="text-center">
          <i className="ri-question-line text-5xl text-charcoal/20 block mb-4" />
          <p className="font-serif text-deep-green text-xl mb-2">カテゴリが見つかりません</p>
          <Link to="/categories" className="text-teal text-sm font-sans cursor-pointer hover:underline">カテゴリ一覧へ →</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ivory">
      <Navbar />
      <main className="pt-20">
        {/* Hero */}
        <section className="relative h-56 md:h-72 overflow-hidden">
          <img src={category.image} alt={category.name} className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-t from-deep-green/90 via-deep-green/50 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 px-4 pb-8">
            <div className="max-w-screen-xl mx-auto">
              <div className="flex items-center gap-3 mb-2">
                <Link to="/categories" className="text-white/60 text-xs font-sans cursor-pointer hover:text-white">カテゴリ一覧</Link>
                <i className="ri-arrow-right-s-line text-white/30 text-xs" />
                <p className="text-white/80 text-xs font-sans">{category.name}</p>
              </div>
              <div className="flex items-end gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-8 h-8 flex items-center justify-center">
                      <i className={`${category.icon} text-gold text-xl`} />
                    </div>
                    <h1 className="font-serif text-white text-3xl md:text-4xl font-light">{category.name}</h1>
                  </div>
                  <p className="text-white/70 text-sm font-sans">{category.description}</p>
                </div>
                <div className="hidden md:flex gap-6 flex-shrink-0">
                  <div className="text-center">
                    <p className="font-serif text-white text-2xl">{providers.length}</p>
                    <p className="text-white/50 text-xs font-sans">事業者</p>
                  </div>
                  <div className="text-center">
                    <p className="font-serif text-white text-2xl">{courses.length}</p>
                    <p className="text-white/50 text-xs font-sans">講座・教材</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Tabs */}
        <section className="bg-white border-b border-stone-100 sticky top-16 z-30 px-4">
          <div className="max-w-screen-xl mx-auto flex items-center justify-between">
            <div className="flex gap-1">
              {([
                { id: "providers", label: `事業者 (${providers.length})`, icon: "ri-store-2-line" },
                { id: "courses", label: `講座・教材 (${courses.length})`, icon: "ri-play-circle-line" }
              ] as const).map((t) => (
                <button key={t.id} onClick={() => setActiveTab(t.id)}
                  className={`flex items-center gap-2 px-5 py-4 text-sm font-sans cursor-pointer border-b-2 transition-all whitespace-nowrap ${activeTab === t.id ? "border-deep-green text-deep-green" : "border-transparent text-charcoal/50 hover:text-charcoal"}`}>
                  <i className={t.icon} />{t.label}
                </button>
              ))}
            </div>
            <div className="hidden sm:flex gap-2">
              {[{ v: "popular", l: "人気順" }, { v: "rating", l: "評価順" }].map((s) => (
                <button key={s.v} onClick={() => setSort(s.v)}
                  className={`px-3 py-1.5 rounded-full text-xs font-sans whitespace-nowrap cursor-pointer border transition-all ${sort === s.v ? "bg-deep-green text-white border-deep-green" : "border-stone-200 text-charcoal/50 hover:border-teal"}`}>{s.l}</button>
              ))}
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="px-4 py-8">
          <div className="max-w-screen-xl mx-auto">
            {activeTab === "providers" && (
              <>
                {sortedProviders.length === 0 ? (
                  <div className="text-center py-20">
                    <i className="ri-store-2-line text-5xl text-charcoal/20 block mb-4" />
                    <p className="text-charcoal/40 font-sans">このカテゴリの事業者はまだいません</p>
                    <Link to="/providers" className="mt-3 inline-block text-teal text-sm font-sans cursor-pointer hover:underline">すべての事業者を見る →</Link>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {sortedProviders.map((p) => {
                      const avatar = p.avatar_url || `https://public.readdy.ai/ai/img_res/01ee8e9e8b7546a55f668f594385f30d.jpg${p.id.slice(0,8)}&orientation=squarish`;
                      const cover = p.cover_image_url || `https://readdy.ai/api/search-image?query=Japanese%20craft%20workshop%20artisan%20elegant%20studio&width=480&height=280&seq=cdet-cv-${p.id.slice(0,8)}&orientation=landscape`;
                      return (
                        <div key={p.id} className="bg-white rounded-2xl overflow-hidden card-hover border border-stone-100">
                          <div className="relative h-44 overflow-hidden group">
                            <img src={cover} alt={p.service_name ?? p.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                            <div className="absolute inset-0 bg-gradient-to-t from-deep-green/60 to-transparent" />
                            <div className="absolute bottom-3 left-4 flex items-end gap-2">
                              <img src={avatar} alt={p.name} className="w-12 h-12 rounded-full border-2 border-white object-cover" />
                              <div>
                                <p className="text-white font-serif text-sm">{p.service_name ?? p.name}</p>
                                <p className="text-white/60 text-xs font-sans">{p.location ?? "日本"}</p>
                              </div>
                            </div>
                          </div>
                          <div className="p-4">
                            {p.slogan && <p className="font-display italic text-teal text-xs mb-2">"{p.slogan}"</p>}
                            {p.skills && p.skills.length > 0 && (
                              <div className="flex flex-wrap gap-1 mb-3">
                                {p.skills.slice(0, 3).map((s) => <span key={s} className="tag-pill">{s}</span>)}
                              </div>
                            )}
                            <div className="flex items-center gap-3 text-xs text-charcoal/50 font-sans mb-3">
                              <span><span className="star-gold">★</span> {(p.rating ?? 0).toFixed(1)} ({p.review_count ?? 0})</span>
                              <span><i className="ri-user-follow-line" /> {(p.follower_count ?? 0).toLocaleString()}</span>
                            </div>
                            <Link to={`/providers/${p.id}`} className="block w-full text-center py-2.5 rounded-full bg-deep-green text-white text-xs font-sans cursor-pointer hover:bg-teal transition-colors whitespace-nowrap">
                              詳細を見る →
                            </Link>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
            {activeTab === "courses" && (
              <>
                {courses.length === 0 ? (
                  <div className="text-center py-20">
                    <i className="ri-play-circle-line text-5xl text-charcoal/20 block mb-4" />
                    <p className="text-charcoal/40 font-sans">このカテゴリの講座はまだありません</p>
                    <Link to="/providers" className="mt-3 inline-block text-teal text-sm font-sans cursor-pointer hover:underline">事業者を探す →</Link>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                    {courses.map((prod) => (
                      <Link key={prod.id} to={`/${prod.type === "video" ? "courses" : "materials"}/${prod.id}`} className="bg-white rounded-2xl overflow-hidden card-hover border border-stone-100 block">
                        <div className="h-36 overflow-hidden relative">
                          {prod.thumbnail_url ? (
                            <img src={prod.thumbnail_url} alt={prod.title} className="w-full h-full object-cover object-top" />
                          ) : (
                            <div className="w-full h-full bg-stone-100 flex items-center justify-center">
                              <i className={`${prod.type === "video" ? "ri-video-line" : "ri-box-3-line"} text-3xl text-charcoal/20`} />
                            </div>
                          )}
                          <span className={`absolute top-2 left-2 text-xs px-2 py-0.5 rounded-full font-sans ${prod.type === "video" ? "bg-teal text-white" : "bg-gold text-white"}`}>
                            {prod.type === "video" ? "動画" : "教材"}
                          </span>
                        </div>
                        <div className="p-4">
                          <p className="font-sans text-sm text-charcoal leading-snug mb-1 line-clamp-2">{prod.title}</p>
                          <p className="text-xs text-charcoal/40 font-sans mb-2">
                            {(prod.provider as { service_name?: string; name?: string } | null)?.service_name || (prod.provider as { name?: string } | null)?.name || "事業者"}
                          </p>
                          <p className="font-serif text-deep-green text-base">
                            {prod.price === 0 ? <span className="text-teal">無料サンプル</span> : `¥${prod.price.toLocaleString()}`}
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
